// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import {
  formatDateTime,
  formatDuration,
  formatPhoneNumber,
  getDriverInfo,
  getLatestRidehail,
  getRideDurationMinutes,
  type PickedLocation,
} from '../utils/guestRideUtils';
import { useCustomerConfig } from '@store';

import { Avatar, Box, Button, Typography } from '@mui/material';
import { BiLinkAlt, BiLinkExternal } from 'react-icons/bi';
import { Map, useMap } from '@vis.gl/react-google-maps';
import { LocationPinMarker, getMidpointCenter, getZoomForSpan } from '@components/Maps';

import Modal from '@components/ModalComponents/Modal';
import ModalHeader from '@components/ModalComponents/ModalHeader';
import ModalContent from '@components/ModalComponents/ModalContent';
import ModalFooter from '@components/ModalComponents/ModalFooter';

import GuestRideStatusBadge from '@components/Badges/GuestRideStatusBadge';
import GuestRideProviderMark from './GuestRideProviderMark';
import { generateAndCopyGuestLink } from '../services/guestLinkService';

// TYPES ---------------------------------------------------------------- //

interface GuestRideDetailsModalProps {
  open: boolean;
  onClose: () => void;
  ride?: any;
}

// HELPERS ---------------------------------------------------------------- //

/** Build a PickedLocation from a ride's address/lat/lng triplet, or null when incomplete. */
const toLocation = (
  address?: string,
  lat?: string | number | null,
  lng?: string | number | null,
): PickedLocation | null => {
  if (!address || lat == null || lng == null) return null;
  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (Number.isNaN(latNum) || Number.isNaN(lngNum)) return null;
  return { address, lat: latNum, lng: lngNum };
};

/** Uppercase section label, matching the KpiCard / DetailStat overline treatment. */
const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant='overline' sx={{ color: 'grey.500', display: 'block', mb: 0.75 }}>
    {children}
  </Typography>
);

/** Label + value row for the read-only detail list. */
const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 2, py: 0.5, alignItems: 'baseline' }}>
    <Typography
      variant='caption'
      sx={{ color: 'text.secondary', width: 96, flexShrink: 0, textTransform: 'uppercase', fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Typography variant='body2' sx={{ fontWeight: 500, minWidth: 0, wordBreak: 'break-word' }}>
      {value || '—'}
    </Typography>
  </Box>
);

// COMPONENT ---------------------------------------------------------------- //

const GuestRideDetailsModal: React.FC<GuestRideDetailsModalProps> = ({ open, onClose, ride }) => {
  const customerConfig = useCustomerConfig();
  const refLabel = customerConfig?.reference_num_label || 'Reference ID';

  const ridehail = getLatestRidehail(ride);
  const driverInfo = getDriverInfo(ride);
  const durationMinutes = getRideDurationMinutes(ride);

  const pickup = toLocation(ride?.pickup_address, ride?.pickup_lat, ride?.pickup_lng);
  const dropoff = toLocation(ride?.dropoff_address, ride?.dropoff_lat, ride?.dropoff_lng);
  const hasMap = !!(pickup || dropoff);

  const [vehicleImgError, setVehicleImgError] = React.useState(false);
  const [copying, setCopying] = React.useState(false);

  const handleCopyLink = async () => {
    if (!ride?.id) return;
    setCopying(true);
    try {
      await generateAndCopyGuestLink(ride.id, 'guestride');
    } finally {
      setCopying(false);
    }
  };

  const map = useMap('guest-ride-details-map');
  const pLat = pickup?.lat;
  const pLng = pickup?.lng;
  const dLat = dropoff?.lat;
  const dLng = dropoff?.lng;

  // Frame the map so both pins are always visible (fit to both, or center the one present).
  React.useEffect(() => {
    if (!map || !window.google?.maps) return;
    if (pLat != null && pLng != null && dLat != null && dLng != null) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend({ lat: pLat, lng: pLng });
      bounds.extend({ lat: dLat, lng: dLng });
      map.fitBounds(bounds, 48);
    } else if (pLat != null && pLng != null) {
      map.panTo({ lat: pLat, lng: pLng });
      map.setZoom(14);
    } else if (dLat != null && dLng != null) {
      map.panTo({ lat: dLat, lng: dLng });
      map.setZoom(14);
    }
  }, [map, pLat, pLng, dLat, dLng]);

  const timeline = React.useMemo(() => {
    const segments: { label: string; value: string }[] = [];
    if (ridehail?.created_at) segments.push({ label: 'Time Called', value: formatDateTime(ridehail.created_at) });
    if (ridehail?.pickup_at) segments.push({ label: 'ETA', value: formatDateTime(ridehail.pickup_at) });
    if (durationMinutes != null) segments.push({ label: 'Duration', value: formatDuration(durationMinutes) });
    return segments;
  }, [ridehail?.created_at, ridehail?.pickup_at, durationMinutes]);

  const productTypeLabels: Record<string, string> = {
    STANDARD: 'Standard',
    XL: 'XL',
    LUXURY: 'Luxury',
    LUXURY_XL: 'Luxury XL',
  };
  const productType = ride?.config?.productType;
  const productTypeLabel = productType ? productTypeLabels[productType] ?? productType : undefined;
  const referenceNum = ride?.config?.reference_num;
  const notes = ride?.notes;

  const trackingLink: string | null = ridehail?.tracking_link || null;
  const providerLabel = (() => {
    const p = (ridehail?.provider || ride?.provider || '').toLowerCase();
    if (p.includes('uber')) return 'Uber';
    if (p.includes('lyft')) return 'Lyft';
    return null;
  })();

  return (
    <Modal open={open} handleClose={onClose} width='sm'>
      <ModalHeader handleClose={onClose}>Ride Details</ModalHeader>

      <ModalContent>
        {/* Status + provider */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 2 }}>
          <GuestRideStatusBadge status={ride?.status} />
          <GuestRideProviderMark provider={ridehail?.provider || ride?.provider} />
        </Box>

        {/* Guest */}
        <Box sx={{ mb: 2 }}>
          <Typography variant='h4' sx={{ fontSize: 18, fontWeight: 600, lineHeight: 1.25 }}>
            {ride?.guest_name || 'Unknown Guest'}
          </Typography>
          <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
            {formatPhoneNumber(ride?.guest_phone)}
          </Typography>
        </Box>

        {/* Route */}
        <SectionLabel>Route</SectionLabel>
        <Box sx={{ display: 'flex', gap: 1.5, mb: hasMap ? 1.5 : 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5, flexShrink: 0 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'utility.main' }} />
            <Box sx={{ width: 2, height: 17, bgcolor: 'divider', my: 0.25 }} />
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'utility.lighter' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant='body2' sx={{ lineHeight: 1.25 }}>
              {ride?.pickup_address || 'Pickup address pending'}
            </Typography>
            <Typography variant='body2' sx={{ mt: 1.75, lineHeight: 1.25 }}>
              {ride?.dropoff_address || 'Dropoff address pending'}
            </Typography>
          </Box>
        </Box>

        {/* Map preview */}
        {hasMap && (
          <Box sx={{ height: 220, borderRadius: '8px', overflow: 'hidden', border: '1px solid', borderColor: 'utility.lighter', mb: 2 }}>
            <Map
              id='guest-ride-details-map'
              mapId='a1bb97db90b89ef9'
              defaultCenter={getMidpointCenter(pickup, dropoff)}
              defaultZoom={getZoomForSpan(pickup, dropoff)}
              disableDefaultUI
              gestureHandling='cooperative'
              clickableIcons={false}
              mapTypeControl={false}
              streetViewControl={false}
              fullscreenControl={false}
              style={{ width: '100%', height: '100%' }}
            >
              {pickup && (
                <LocationPinMarker
                  mapId='guest-ride-details-map'
                  markerId='pickup'
                  position={{ lat: pickup.lat, lng: pickup.lng }}
                  size={40}
                  iconSize={22}
                />
              )}
              {dropoff && (
                <LocationPinMarker
                  mapId='guest-ride-details-map'
                  markerId='delivery'
                  position={{ lat: dropoff.lat, lng: dropoff.lng }}
                  size={40}
                  iconSize={22}
                />
              )}
            </Map>
          </Box>
        )}

        {/* Timeline */}
        {timeline.length > 0 && (
          <Box sx={{ bgcolor: 'grey.50', borderRadius: '8px', px: 1.5, py: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
              {timeline.map(seg => (
                <Box key={seg.label} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: 'text.secondary',
                      fontSize: 10,
                      fontWeight: 500,
                      lineHeight: 1.25,
                      mb: 0.25,
                      textAlign: 'center',
                      textTransform: 'uppercase',
                    }}
                  >
                    {seg.label}
                  </Typography>
                  <Typography variant='body2' sx={{ fontWeight: 500, lineHeight: 1.25, textAlign: 'center' }}>
                    {seg.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Trip details */}
        <SectionLabel>Details</SectionLabel>
        <Box sx={{ mb: 2 }}>
          <Field label='Product' value={productTypeLabel} />
          <Field label={refLabel} value={referenceNum} />
        </Box>

        {/* Driver + Vehicle */}
        {(driverInfo?.name || driverInfo?.vehicle || driverInfo?.licensePlate || driverInfo?.vehiclePhotoUrl) && (
          <Box sx={{ display: 'flex', gap: 2, mb: notes ? 2 : 0 }}>
            {driverInfo?.name && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <SectionLabel>Driver</SectionLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    src={driverInfo.photoUrl || undefined}
                    alt={driverInfo.name}
                    sx={{ width: 40, height: 40, flexShrink: 0 }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant='body2' sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                      {driverInfo.name}
                    </Typography>
                    {driverInfo.phone && (
                      <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>
                        {formatPhoneNumber(driverInfo.phone)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}

            {(driverInfo?.vehicle || driverInfo?.licensePlate || driverInfo?.vehiclePhotoUrl) && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <SectionLabel>Vehicle</SectionLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: '4px',
                      bgcolor: 'grey.50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    {driverInfo?.vehiclePhotoUrl && !vehicleImgError && (
                      <Box
                        component='img'
                        src={driverInfo.vehiclePhotoUrl}
                        alt={driverInfo.vehicle || 'Vehicle'}
                        onError={() => setVehicleImgError(true)}
                        sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                      />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {driverInfo?.vehicle && (
                      <Typography variant='body2' sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {driverInfo.vehicle}
                      </Typography>
                    )}
                    {driverInfo?.licensePlate && (
                      <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>
                        {driverInfo.licensePlate}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Notes */}
        {notes && (
          <>
            <SectionLabel>Notes</SectionLabel>
            <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {notes}
            </Typography>
          </>
        )}
      </ModalContent>

      <ModalFooter>
        <Button variant='text' color='utility' onClick={onClose}>
          Close
        </Button>
        <Button
          variant='outlined'
          color='primary'
          startIcon={<BiLinkAlt />}
          onClick={handleCopyLink}
          disabled={copying || !ride?.id}
        >
          {copying ? 'Copying...' : 'Copy Guest Link'}
        </Button>
        {trackingLink && (
          <Button
            variant='contained'
            color='primary'
            endIcon={<BiLinkExternal />}
            onClick={() => window.open(trackingLink, '_blank', 'noopener,noreferrer')}
          >
            {providerLabel ? `View in ${providerLabel}` : 'View Ride'}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default GuestRideDetailsModal;
